import { useEffect, useState } from 'react';
import Modal from './Modal';
import { fmtVND } from '../utils/format';
import { customerRevenueSeries } from '../api/cumulativeRevenue';

// Biểu đồ đường doanh thu theo tháng của 1 KH + đường trung bình. Bấm điểm để xem chi tiết.
function LineChart({ points, average }) {
  const [sel, setSel] = useState(null);
  const W = 920;
  const H = 320;
  const padL = 70;
  const padR = 20;
  const padT = 16;
  const padB = 48;
  const n = points.length;
  const max = Math.max(1, average, ...points.map((p) => p.value));
  const x = (i) => (n <= 1 ? padL : padL + (i * (W - padL - padR)) / (n - 1));
  const y = (v) => padT + (1 - v / max) * (H - padT - padB);
  const grid = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const step = Math.max(1, Math.ceil(n / 16));
  const fmtShort = (v) => (v >= 1e9 ? (v / 1e9).toFixed(1) + 'T' : v >= 1e6 ? Math.round(v / 1e6) + 'tr' : String(v));

  let tip = null;
  if (sel != null && points[sel]) {
    const bw = 170;
    const bh = 44;
    let bx = x(sel) + 10;
    if (bx + bw > W) bx = x(sel) - bw - 10;
    const by = Math.max(2, y(points[sel].value) - bh - 8);
    tip = (
      <g pointerEvents="none">
        <line x1={x(sel)} y1={padT} x2={x(sel)} y2={H - padB} stroke="var(--muted)" strokeDasharray="3 3" />
        <rect x={bx} y={by} width={bw} height={bh} rx="8" fill="var(--panel)" stroke="var(--border)" />
        <text x={bx + 10} y={by + 18} fontSize="12" fontWeight="700" fill="var(--text)">{points[sel].label}</text>
        <text x={bx + 10} y={by + 35} fontSize="12" fill="#5b8cff" fontWeight="700">{fmtVND(points[sel].value)}</text>
      </g>
    );
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} onClick={() => setSel(null)}>
      {grid.map((g, i) => {
        const yy = y(g);
        return (
          <g key={i}>
            <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="var(--border)" strokeWidth="1" />
            <text x={padL - 8} y={yy + 4} textAnchor="end" fontSize="11" fill="var(--muted)">
              {fmtShort(g)}
            </text>
          </g>
        );
      })}
      {points.map((p, i) =>
        i % step === 0 ? (
          <text key={i} x={x(i)} y={H - 28} textAnchor="middle" fontSize="10" fill="var(--muted)" transform={`rotate(-45 ${x(i)} ${H - 28})`}>
            {p.label}
          </text>
        ) : null
      )}
      {/* Đường trung bình (gạch nối) */}
      <line x1={padL} y1={y(average)} x2={W - padR} y2={y(average)} stroke="#f59e0b" strokeWidth="2" strokeDasharray="6 4" />
      <text x={W - padR} y={y(average) - 5} textAnchor="end" fontSize="11" fill="#f59e0b" fontWeight="700">
        TB {fmtShort(average)}
      </text>
      {/* Đường doanh thu */}
      <polyline points={points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ')} fill="none" stroke="#5b8cff" strokeWidth="2.5" />
      {points.map((p, i) => (
        <circle key={i} cx={x(i)} cy={y(p.value)} r={sel === i ? 4.5 : 2.5} fill="#5b8cff" />
      ))}
      {/* vùng bấm rộng cho mỗi điểm */}
      {points.map((p, i) => (
        <rect
          key={'h' + i}
          x={x(i) - 8}
          y={padT}
          width={16}
          height={H - padT - padB}
          fill="transparent"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            setSel(i);
          }}
        />
      ))}
      {tip}
    </svg>
  );
}

export default function CustomerRevenueChartModal({ open, code, name, onClose }) {
  const [metric, setMetric] = useState('total');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !code) return;
    setLoading(true);
    customerRevenueSeries({ code, metric })
      .then(setData)
      .finally(() => setLoading(false));
  }, [open, code, metric]);

  const metricLabel = { mh: 'Mua hộ', vc: 'Vận chuyển', total: 'Tổng (Mua hộ + VC)' };

  return (
    <Modal open={open} onClose={onClose} wide>
      <h3 style={{ marginTop: 0 }}>📈 Doanh thu theo tháng — {name || code}</h3>
      <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <span className="small muted">
          {data?.years?.length ? `Năm: ${data.years.join(', ')}` : 'Mã KH: ' + code}
        </span>
        <div className="seg">
          {['mh', 'vc', 'total'].map((m) => (
            <button key={m} className={metric === m ? 'active' : ''} onClick={() => setMetric(m)}>
              {metricLabel[m]}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="empty" style={{ padding: 40 }}>
          Đang tải...
        </div>
      ) : !data || !data.points.length ? (
        <div className="empty" style={{ padding: 40, textAlign: 'center' }}>
          Chưa có dữ liệu doanh thu để hiển thị.
          {data && !data.canViewAll ? (
            <div className="small muted" style={{ marginTop: 6 }}>
              Nhân viên chỉ xem được biểu đồ doanh thu từ năm 2026.
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <LineChart points={data.points} average={data.average} />
          <div className="row" style={{ gap: 20, marginTop: 10, justifyContent: 'center' }}>
            <span className="small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 16, height: 3, background: '#5b8cff', display: 'inline-block' }} /> Doanh thu / tháng
            </span>
            <span className="small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 16, height: 3, background: '#f59e0b', display: 'inline-block' }} /> Trung bình ({fmtVND(data.average)})
            </span>
          </div>
        </>
      )}
    </Modal>
  );
}
